"use client";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger } from "~/components/v4/shadcn/Tabs";
import { GetHomeEvents, getHomeEvents } from "../actions";
import { HomeEventsList } from "./HomeEventsList";
import { ToggleGroup } from "~/components/v4/themed/ToggleGroup";
import { Button } from "~/components/v4/themed/Button";
import { cn } from "~/utils/fontsConfig";
import { GetCommunitiesToHost } from "../../events/create/actions";
import { GetCurrentUserProfile } from "../../events/create/actions";

type Props = {
    initialEvents: GetHomeEvents;
    user: GetCurrentUserProfile;
    communities: GetCommunitiesToHost;
};

const HomeEvents = ({ initialEvents, user, communities }: Props) => {
    const [hasMounted, setHasMounted] = useState(false);
    const [selectedTab, setSelectedTab] = useState<
        "all" | "hosting" | "attending" | "draft"
    >("all");
    const [isPast, setIsPast] = useState(false);
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [hostingEvents, setHostingEvents] = useState<
        NonNullable<GetHomeEvents["hostingEvents"]>
    >(initialEvents.hostingEvents);
    const [attendingEvents, setAttendingEvents] = useState<
        NonNullable<GetHomeEvents["attendingEvents"]>
    >(initialEvents.attendingEvents);
    const [draftEvents, setDraftEvents] = useState<
        NonNullable<GetHomeEvents["draftEvents"]>
    >(initialEvents.draftEvents);
    const [hasMoreHosting, setHasMoreHosting] = useState<
        GetHomeEvents["hasMoreHosting"]
    >(initialEvents.hasMoreHosting);
    const [hasMoreAttending, setHasMoreAttending] = useState<
        GetHomeEvents["hasMoreAttending"]
    >(initialEvents.hasMoreAttending);
    const [hasMoreDraft, setHasMoreDraft] = useState<
        GetHomeEvents["hasMoreDraft"]
    >(initialEvents.hasMoreDraft);
    const [skipHosting, setSkipHosting] = useState(0);
    const [skipAttending, setSkipAttending] = useState(0);
    const [skipDraft, setSkipDraft] = useState(0);

    const { data, isRefetching } = useQuery({
        queryKey: [
            "homeEvents",
            user.id,
            isPast,
            skipHosting,
            skipAttending,
            skipDraft,
            selectedTab,
        ],
        queryFn: () => {
            const fetchHosting =
                selectedTab === "all" || selectedTab === "hosting";
            const fetchAttending =
                selectedTab === "all" || selectedTab === "attending";
            const fetchDraft = selectedTab === "all" || selectedTab === "draft";
            return getHomeEvents({
                userId: user.id,
                isPast,
                take: 4,
                skipHosting,
                skipAttending,
                skipDraft,
                fetchHosting,
                fetchAttending,
                fetchDraft,
            });
        },
        refetchOnWindowFocus: false,
        initialData:
            isPast === false &&
            skipHosting === 0 &&
            skipAttending === 0 &&
            skipDraft === 0
                ? initialEvents
                : undefined,
        onSuccess: (newData) => {
            if (selectedTab === "all" || selectedTab === "hosting") {
                setHostingEvents((prev) => {
                    const prevArray = Array.isArray(prev) ? prev : [];
                    const newEvents = newData?.hostingEvents ?? [];
                    // Use Map to deduplicate events by ID
                    const combinedEvents = [...prevArray, ...newEvents];
                    return Array.from(
                        new Map(
                            combinedEvents.map((event) => [event.id, event]),
                        ).values(),
                    );
                });
                setHasMoreHosting(newData?.hasMoreHosting ?? false);
            }
            if (selectedTab === "all" || selectedTab === "attending") {
                setAttendingEvents((prev) => {
                    const prevArray = Array.isArray(prev) ? prev : [];
                    const newEvents = newData?.attendingEvents ?? [];
                    // Use Map to deduplicate events by ID
                    const combinedEvents = [...prevArray, ...newEvents];
                    return Array.from(
                        new Map(
                            combinedEvents.map((event) => [event.id, event]),
                        ).values(),
                    );
                });
                setHasMoreAttending(newData?.hasMoreAttending ?? false);
            }
            if (selectedTab === "all" || selectedTab === "draft") {
                setDraftEvents((prev) => {
                    const prevArray = Array.isArray(prev) ? prev : [];
                    const newEvents = newData?.draftEvents ?? [];
                    // Use Map to deduplicate events by ID
                    const combinedEvents = [...prevArray, ...newEvents];
                    return Array.from(
                        new Map(
                            combinedEvents.map((event) => [event.id, event]),
                        ).values(),
                    );
                });
                setHasMoreDraft(newData?.hasMoreDraft ?? false);
            }
            // REMOVED: redundant state updates that were overriding the previous logic
        },
    });

    useEffect(() => {
        setHasMounted(true);
    }, []);

    // Reset state when isPast or selectedTab changes, but skip initial render
    useEffect(() => {
        if (!hasMounted && isPast === false) return; // Skip on initial render

        // Reset all state variables when toggling between past/upcoming
        setHostingEvents([]);
        setAttendingEvents([]);
        setDraftEvents([]); // Added this line to reset draft events too

        // Reset skip counters
        setSkipHosting(0);
        setSkipAttending(0);
        setSkipDraft(0);
    }, [isPast, selectedTab, hasMounted]);

    // Create a deduplicated list of events based on the selected tab
    const eventsToDisplay = useMemo(() => {
        if (selectedTab === "hosting") return hostingEvents;
        if (selectedTab === "attending") return attendingEvents;
        if (selectedTab === "draft") return draftEvents;
        // For "all" tab, deduplicate by event ID to prevent duplicates
        const allEvents = [
            ...hostingEvents,
            ...attendingEvents,
            ...draftEvents,
        ];
        const uniqueEvents = Array.from(
            new Map(allEvents.map((event) => [event.id, event])).values(),
        );
        return uniqueEvents;
    }, [hostingEvents, attendingEvents, draftEvents, selectedTab, isPast]);

    const finalEventsToDisplay = !hasMounted
        ? Array.from(
              new Map(
                  [
                      ...initialEvents.hostingEvents,
                      ...initialEvents.attendingEvents,
                      ...initialEvents.draftEvents,
                  ].map((event) => [event.id, event]),
              ).values(),
          )
        : eventsToDisplay;

    const hasMore =
        selectedTab === "all"
            ? hasMoreHosting || hasMoreAttending || hasMoreDraft // Added hasMoreDraft
            : selectedTab === "hosting"
              ? hasMoreHosting
              : selectedTab === "attending"
                ? hasMoreAttending
                : hasMoreDraft; // Added case for draft tab

    const handleShowMore = () => {
        if (selectedTab === "all" || selectedTab === "hosting") {
            setSkipHosting(hostingEvents?.length ?? 0);
        }
        if (selectedTab === "all" || selectedTab === "attending") {
            setSkipAttending(attendingEvents?.length ?? 0);
        }
        if (selectedTab === "all" || selectedTab === "draft") {
            setSkipDraft(draftEvents?.length ?? 0); // Added this to load more draft events
        }
    };

    return (
        <div className="flex w-full flex-col gap-4">
            <Tabs
                value={selectedTab}
                onValueChange={(value) =>
                    setSelectedTab(value as typeof selectedTab)
                }
            >
                <TabsList>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="hosting">Hosting</TabsTrigger>
                    <TabsTrigger value="attending">Attending</TabsTrigger>
                    <TabsTrigger value="draft">Draft</TabsTrigger>
                </TabsList>
            </Tabs>
            <div className="flex flex-row items-center gap-2">
                <ToggleGroup>
                    <Button
                        variant="tabPrimary"
                        className={cn(
                            !isPast &&
                                "rounded-lg bg-white font-[500] text-black hover:ring-0",
                        )}
                        onClick={() => setIsPast(false)}
                    >
                        Upcoming
                    </Button>
                    <Button
                        variant="tabPrimary"
                        className={cn(
                            isPast &&
                                "bg-white font-[500] text-black  hover:ring-0",
                        )}
                        onClick={() => setIsPast(true)}
                    >
                        Past
                    </Button>
                </ToggleGroup>
                {/*
                    <ToggleGroup>
                        <Button
                            variant="tabPrimary"
                            className={cn(
                                viewMode === "grid" && "bg-white text-black",
                            )}
                            onClick={() => setViewMode("grid")}
                        >
                            <FaThLarge />
                        </Button>
                        <Button
                            variant="tabPrimary"
                            className={cn(
                                viewMode === "list" && "bg-white text-black",
                            )}
                            onClick={() => setViewMode("list")}
                        >
                            <FaList />
                        </Button>
                    </ToggleGroup>
                */}
            </div>
            <HomeEventsList
                user={user}
                events={finalEventsToDisplay}
                hasMore={hasMore}
                onShowMore={handleShowMore}
                isLoading={isRefetching}
                isPast={isPast}
                communities={communities}
            />
        </div>
    );
};

export default HomeEvents;
